import { StyleSheet, css } from 'aphrodite'
import type React from 'react'

interface ConfirmationDialogProps {
  /** The title of the dialog */
  title: string
  /** The message to display */
  message: string
  /** Label for the confirm button */
  confirmLabel: string
  /** Label for the cancel button */
  cancelLabel: string
  /** Whether the confirm button is in a loading state */
  isLoading?: boolean
  /** Called when user confirms */
  onConfirm: () => void
  /** Called when user cancels */
  onCancel: () => void
  /** Whether the confirm button should be styled as destructive (red) */
  isDestructive?: boolean
  /** Whether to reverse the button order (confirm on left, cancel on right) */
  reverseButtons?: boolean
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  title,
  message,
  confirmLabel,
  cancelLabel,
  isLoading = false,
  onConfirm,
  onCancel,
  isDestructive = false,
  reverseButtons = false,
}) => {
  return (
    <div className={css(styles.overlay)}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h3 className={css(styles.title)}>{title}</h3>
        </div>
        <div className={css(styles.content)}>
          <p className={css(styles.message)}>{message}</p>
        </div>
        <div
          className={css(
            styles.footer,
            reverseButtons && styles.footerReversed,
          )}
        >
          <button
            onClick={onCancel}
            className={css(styles.cancelBtn)}
            type="button"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={css(
              styles.confirmBtn,
              isDestructive && styles.confirmBtnDestructive,
            )}
            disabled={isLoading}
            type="button"
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '450px',
    margin: '20px',
  },
  header: {
    padding: '24px 24px 0 24px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  content: {
    padding: '16px 24px',
  },
  message: {
    margin: 0,
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  footer: {
    padding: '16px 24px 24px 24px',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  footerReversed: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
  },
  cancelBtn: {
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
  },
  confirmBtn: {
    backgroundColor: '#10B981',
    color: '#000000',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmBtnDestructive: {
    backgroundColor: '#DC2626',
    color: 'white',
    ':hover': {
      backgroundColor: '#B91C1C',
    },
  },
})

export default ConfirmationDialog
